import ProjectForm from "@/components/projects/ProjectForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import companyService from "@/services/company";
import projectService from "@/services/project";
import { Company } from "@/types/company";
import { Project } from "@/types/project";
import { ArrowLeft, MoreHorizontal, Pencil, PlusCircle, Search, Settings } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function Projects() {
    const { toast } = useToast();
    const navigate = useNavigate();
    const { companyId } = useParams<{ companyId: string }>();

    const [projects, setProjects] = useState<Project[]>([]);
    const [company, setCompany] = useState<Company | null>(null);
    const [totalProjects, setTotalProjects] = useState(0);
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [editingProject, setEditingProject] = useState<Project | undefined>(undefined);

    const fetchCompany = async () => {
        if (!companyId) return;

        try {
            const [data, error] = await companyService.getById(companyId);

            if (error) {
                toast({
                    title: "Erro",
                    description: error.msg || "Erro ao carregar empresa",
                    variant: "destructive",
                });
                return;
            }

            setCompany(data);
        } catch (error) {
            toast({
                title: "Erro",
                description: "Falha ao carregar a empresa",
                variant: "destructive",
            });
        }
    };

    const fetchProjects = async () => {
        if (!companyId) return;

        setIsLoading(true);
        try {
            const [data, error] = await projectService.getAllByClient(companyId, search, page, limit);

            if (error) {
                toast({
                    title: "Erro",
                    description: error.msg || "Erro ao carregar projetos",
                    variant: "destructive",
                });
                setProjects([]);
                setTotalProjects(0);
                return;
            }

            if (data && data.result) {
                setProjects(data.result);
                setTotalProjects(data.total || 0);
            } else {
                // Caso a resposta não tenha o formato esperado
                console.error("Resposta da API em formato inesperado:", data);
                setProjects([]);
                setTotalProjects(0);
                toast({
                    title: "Erro",
                    description: "Formato de resposta inesperado ao carregar projetos",
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error("Erro ao carregar projetos:", error);
            setProjects([]);
            setTotalProjects(0);
            toast({
                title: "Erro",
                description: "Falha ao carregar os projetos",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCompany();
    }, [companyId]);

    useEffect(() => {
        fetchProjects();
    }, [companyId, page, limit, search]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };

    const handleCreateProject = () => {
        setFormMode("create");
        setEditingProject(undefined);
        setFormOpen(true);
    };

    const handleEditProject = (project: Project) => {
        setFormMode("edit");
        setEditingProject(project);
        setFormOpen(true);
    };

    const handleViewSLAs = (project: Project) => {
        navigate(`/companies/${companyId}/projects/${project._id}/slas`);
    };

    const handleBackToCompanies = () => {
        navigate("/companies");
    };

    if (!companyId) {
        return <div>Empresa não encontrada</div>;
    }

    return (
        <>
            <div className="flex items-center mb-6">
                <Button variant="ghost" onClick={handleBackToCompanies} className="mr-4">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Voltar
                </Button>
                <h1 className="text-2xl font-bold flex-1">
                    Projetos {company && `de ${company.name}`}
                </h1>
                <Button onClick={handleCreateProject}>
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Novo Projeto
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Lista de Projetos</CardTitle>
                    <CardDescription>
                        Gerencie os projetos e acesse seus SLAs
                    </CardDescription>
                    <div className="flex items-center mt-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="search"
                                placeholder="Buscar projetos..."
                                className="pl-8"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nome</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-24 text-right">Ações</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {!projects || projects.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                                        {isLoading
                                            ? "Carregando projetos..."
                                            : "Nenhum projeto encontrado"}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                projects.map((project) => (
                                    <TableRow key={project._id}>
                                        <TableCell className="font-medium">{project.name}</TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={project.isActive ? "default" : "secondary"}
                                            >
                                                {project.isActive ? "Ativo" : "Inativo"}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="sm">
                                                        <MoreHorizontal className="h-4 w-4" />
                                                        <span className="sr-only">Abrir menu</span>
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem onClick={() => handleEditProject(project)}>
                                                        <Pencil className="h-4 w-4 mr-2" />
                                                        Editar
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => handleViewSLAs(project)}>
                                                        <Settings className="h-4 w-4 mr-2" />
                                                        Gerenciar SLAs
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
                <CardFooter className="flex justify-between">
                    <div className="text-sm text-muted-foreground">
                        Mostrando {projects?.length || 0} de {totalProjects} projetos
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                            disabled={page === 1 || isLoading}
                        >
                            Anterior
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((prev) => prev + 1)}
                            disabled={!projects || projects.length < limit || isLoading}
                        >
                            Próxima
                        </Button>
                    </div>
                </CardFooter>
            </Card>

            {formOpen && companyId && (
                <ProjectForm
                    open={formOpen}
                    onClose={() => setFormOpen(false)}
                    onSuccess={fetchProjects}
                    initialData={editingProject}
                    mode={formMode}
                    companyId={companyId}
                />
            )}
        </>
    );
} 